import { StandardResponse } from '@/types/common';
import {
  BulkCohortAssignmentPayload,
  HardDeleteUserPayload,
  HardDeleteUserResponse,
  HardDeleteImpactResponse,
  SendWelcomeEmailPayload,
  SendWelcomeEmailResponse,
  TenantUser,
  UpdateUserCohortsPayload,
} from '../types/user';
import axios from './utils/axios';
import generateCRUDOps from './utils/generateCrud';

const pathPrefix = `/org-administration/orgs/`;
const tenantPathPrefix = `/org-administration/tenants/`;
const usersPathPrefix = `/org-administration/users/`;

// Large pages keep the number of round trips low; the loop guard stops a
// misbehaving `next` link from fetching forever.
const TENANT_USERS_PAGE_SIZE = 1000;
const MAX_TENANT_USER_PAGES = 50;

export interface TenantUsersQueryParams {
  page?: number;
  page_size?: number;
}

const UserService = {
  // Use generated CRUD operations
  ...generateCRUDOps(`${pathPrefix}`, 'users'),

  getCurrentUser: async (options?: {
    skipUnauthorizedRedirect?: boolean;
    tenantId?: number;
  }) => {
    const resp = await axios.get(`/auth/me/`, {
      skipUnauthorizedRedirect: options?.skipUnauthorizedRedirect,
      suppressErrorToast: options?.skipUnauthorizedRedirect,
      headers: options?.tenantId
        ? { 'x-tenant-id': options.tenantId.toString() }
        : undefined,
    });
    return resp.data;
  },

  // Add specific organization user endpoints
  createOrgUser: async (orgPk: number, userData: any) => {
    return axios.post(`${pathPrefix}${orgPk}/users/`, userData);
  },

  getUserOrgs: async () => {
    const resp = await axios.get(`${pathPrefix}`);
    return resp.data;
  },

  getOrgUsers: async (orgPk: number) => {
    const resp = await axios.get(`${pathPrefix}${orgPk}/users/`);
    return resp.data;
  },

  getTenantUsers: async (
    tenantId: number,
    params?: TenantUsersQueryParams
  ): Promise<StandardResponse<TenantUser>> => {
    const resp = await axios.get<StandardResponse<TenantUser>>(
      `${tenantPathPrefix}${tenantId}/users/`,
      { params }
    );
    return resp.data;
  },

  /**
   * Fetch every user on the tenant by following the paginated endpoint until
   * there is no next page (one request up to 1000 users), so sorting,
   * filtering and search in User Management run over the full roster.
   */
  getAllTenantUsers: async (
    tenantId: number
  ): Promise<StandardResponse<TenantUser>> => {
    const results: TenantUser[] = [];
    let count = 0;
    let page = 1;
    let hasNext = true;

    while (hasNext && page <= MAX_TENANT_USER_PAGES) {
      const data = await UserService.getTenantUsers(tenantId, {
        page,
        page_size: TENANT_USERS_PAGE_SIZE,
      });
      results.push(...data.results);
      count = data.count;
      hasNext = Boolean(data.next);
      page += 1;
    }

    return { count, next: null, previous: null, results };
  },

  getTenants: async () => {
    const resp = await axios.get(`${tenantPathPrefix}`);
    return resp.data;
  },

  updateOrgUser: async (orgPk: number, userId: number, userData: any) => {
    return axios.patch(`${pathPrefix}${orgPk}/users/${userId}/`, userData);
  },

  updateUser: async (userId: number, payload: UpdateUserCohortsPayload) => {
    const { data } = await axios.patch(`${usersPathPrefix}${userId}/`, payload);
    return data;
  },

  bulkAssignUserCohorts: async (payload: BulkCohortAssignmentPayload) => {
    const { data } = await axios.patch(
      `${usersPathPrefix}cohort-assignment/`,
      payload
    );
    return data;
  },

  sendWelcomeEmails: async (
    payload: SendWelcomeEmailPayload
  ): Promise<SendWelcomeEmailResponse> => {
    const { data } = await axios.post<SendWelcomeEmailResponse>(
      `${usersPathPrefix}welcome-email/`,
      payload
    );
    return data;
  },

  deleteOrgUser: async (orgPk: number, userId: number) => {
    return axios.delete(`${pathPrefix}${orgPk}/users/${userId}/`);
  },

  getHardDeleteImpact: async (
    userId: number
  ): Promise<HardDeleteImpactResponse> => {
    const { data } = await axios.get<HardDeleteImpactResponse>(
      `${usersPathPrefix}${userId}/hard-delete/impact/`
    );
    return data;
  },

  executeHardDelete: async (
    userId: number,
    payload: HardDeleteUserPayload
  ): Promise<HardDeleteUserResponse> => {
    const { data } = await axios.post<HardDeleteUserResponse>(
      `${usersPathPrefix}${userId}/hard-delete/`,
      payload
    );
    return data;
  },

  removeUserFromTenant: async (userId: number): Promise<void> => {
    await axios.delete(`${usersPathPrefix}${userId}/tenant-access/`);
  },
};

export default UserService;
